import { useEffect, useState } from "react";
import { fetchEntityCount } from "@/components/lms/lmsOverviewMetricsHelpers";
import { getStatusOverviewBaseQueryParams } from "@/components/lms/lmsStatusOverviewScope";
import { LmsMode } from "@/components/lms/lms.utils";

export type LmsStatusOverviewStatusKey =
  | "not_started"
  | "in_progress"
  | "completed";

interface OverviewStatusConfig {
  key: LmsStatusOverviewStatusKey;
  color: string;
}

type QueryParams = Record<string, string | number>;

export type LmsStatusOverviewStatusCounts = Record<
  LmsStatusOverviewStatusKey,
  number
>;

interface Params {
  courseId?: string;
  dateFilterParams: Record<string, string>;
  enabled?: boolean;
  filterParams: QueryParams | null;
  mode: LmsMode;
  participantId?: string | number;
  scopedRequestParameters: QueryParams;
  shouldNotImpersonate: boolean;
}

export const LMS_STATUS_OVERVIEW_CONFIGS: OverviewStatusConfig[] = [
  { key: "not_started", color: "#eb6363" },
  { key: "in_progress", color: "#f2be6b" },
  { key: "completed", color: "#5db860" },
];

export const EMPTY_STATUS_COUNTS: LmsStatusOverviewStatusCounts = {
  not_started: 0,
  in_progress: 0,
  completed: 0,
};

export function useLmsStatusOverviewCounts({
  courseId,
  dateFilterParams,
  enabled = true,
  filterParams,
  mode,
  participantId,
  scopedRequestParameters,
  shouldNotImpersonate,
}: Params) {
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState<any>(null);
  const [statusCounts, setStatusCounts] =
    useState<LmsStatusOverviewStatusCounts>(EMPTY_STATUS_COUNTS);

  useEffect(() => {
    let isMounted = true;

    if (!enabled) {
      setIsLoadingOverview(false);
      setOverviewError(null);
      setStatusCounts(EMPTY_STATUS_COUNTS);
      return () => {
        isMounted = false;
      };
    }

    if (mode === "learner" && !participantId) {
      setStatusCounts(EMPTY_STATUS_COUNTS);
      return () => {
        isMounted = false;
      };
    }

    if (filterParams === null) {
      setStatusCounts(EMPTY_STATUS_COUNTS);
      setOverviewError(null);
      return () => {
        isMounted = false;
      };
    }

    const loadCounts = async () => {
      setIsLoadingOverview(true);
      setOverviewError(null);

      try {
        const countValues = await Promise.all(
          LMS_STATUS_OVERVIEW_CONFIGS.map((statusConfig) =>
            fetchEntityCount({
              entity: "enrolment",
              shouldNotImpersonate,
              additionalQueryParams: {
                ...getStatusOverviewBaseQueryParams({
                  courseId,
                  participantId,
                  dateFilterParams,
                  filterParams,
                  status: statusConfig.key,
                }),
                ...scopedRequestParameters,
              },
              rejectOnError: true,
            }),
          ),
        );

        if (!isMounted) {
          return;
        }

        setStatusCounts({
          not_started: countValues[0],
          in_progress: countValues[1],
          completed: countValues[2],
        });
      } catch (requestError) {
        if (!isMounted) {
          return;
        }
        setOverviewError(requestError);
        setStatusCounts(EMPTY_STATUS_COUNTS);
      } finally {
        if (isMounted) {
          setIsLoadingOverview(false);
        }
      }
    };

    loadCounts();

    return () => {
      isMounted = false;
    };
  }, [
    courseId,
    dateFilterParams,
    enabled,
    filterParams,
    mode,
    participantId,
    scopedRequestParameters,
    shouldNotImpersonate,
  ]);

  return {
    clearOverviewError: () => setOverviewError(null),
    isLoadingOverview,
    overviewError,
    statusCounts,
  };
}
